// client/src/services/NotificationService.js

import { EventEmitter } from 'events';
import axios from 'axios';

class NotificationService {
  constructor() {
    this.emitter = new EventEmitter();
    this.hasPermission = false;
    this.audioContext = null;
    this.soundBuffer = null;
    this.initialized = false;
    this.notificationQueue = [];
    this.isProcessingQueue = false;
    this.swRegistration = null;
    
    // Bind methods
    this.processQueue = this.processQueue.bind(this);
    this.handleNotificationClick = this.handleNotificationClick.bind(this);
  }

  async initialize() {
    if (this.initialized) return true;

    try {
      // Check browser notification support
      if (!('Notification' in window)) {
        return false;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      this.hasPermission = permission === 'granted';

      // Initialize Service Worker for notifications
      if ('serviceWorker' in navigator) {
        try {
          this.swRegistration = await navigator.serviceWorker.register(
            '/notification-worker.js',
            { scope: '/' }
          );
        } catch (error) {
          // ServiceWorker registration failed
        }
      }

      // Initialize audio context on user interaction
      document.addEventListener('click', () => {
        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }
      }, { once: true });

      // Start queue processing
      setInterval(this.processQueue, 1000);

      // Add notification click handler
      navigator.serviceWorker.addEventListener('message', this.handleNotificationClick);

      this.initialized = true;
      return this.hasPermission;
    } catch (error) {
      return false;
    }
  }

  async processQueue() {
    if (this.isProcessingQueue || this.notificationQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;
    const notification = this.notificationQueue.shift();

    try {
      await this.showNotification(notification.title, notification.options);
    } catch (error) {
      // Requeue if it's a temporary error
      if (error.name !== 'NotAllowedError') {
        this.notificationQueue.push(notification);
      }
    }

    this.isProcessingQueue = false;
  }

  async playSound() {
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Resume context if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Create and configure oscillator
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configure sound
      oscillator.type = 'sine';
      oscillator.frequency.value = 440; // A4 note
      gainNode.gain.value = 0.1; // Low volume

      // Play sound with fade out
      oscillator.start();
      gainNode.gain.exponentialRampToValueAtTime(
        0.00001,
        this.audioContext.currentTime + 1
      );

      // Cleanup
      setTimeout(() => {
        oscillator.stop();
        oscillator.disconnect();
        gainNode.disconnect();
      }, 1000);
    } catch (error) {
      // Error playing notification sound
    }
  }

  handleNotificationClick(event) {
    const notification = event.notification;
    const data = notification.data;

    // Handle different notification types
    switch (data?.type) {
      case 'ISSUE':
        window.location.href = `/issues/${data.issueId}`;
        break;
      case 'MACHINE':
        window.location.href = `/machines/${data.machineId}`;
        break;
      case 'SAFETY':
        window.location.href = `/safety/${data.incidentId}`;
        break;
      default:
        window.location.href = '/notifications';
    }

    notification.close();
  }

  async showNotification(title, options = {}) {
    if (!this.hasPermission) {
      return null;
    }

    try {
      if (this.swRegistration) {
        // Use Service Worker for notifications when available
        await this.swRegistration.showNotification(title, {
          ...options,
          badge: '/logo192.png',
          icon: '/logo192.png',
          requireInteraction: true,
          silent: true // We handle sound separately
        });
      } else {
        // Fallback to regular notifications
        new Notification(title, {
          ...options,
          badge: '/logo192.png',
          icon: '/logo192.png',
          requireInteraction: true,
          silent: true
        });
      }

      // Play notification sound
      await this.playSound();

      // Vibrate if supported
      if (window.navigator.vibrate) {
        window.navigator.vibrate(200);
      }

      return true;
    } catch (error) {
      throw error;
    }
  }

  async notifyMachineIssue(machine, issue) {
    try {
      // Prepare notification data
      const title = `Machine Issue: ${machine.machineName}`;
      const options = {
        body: issue.description,
        data: {
          type: 'ISSUE',
          machineId: machine._id,
          issueId: issue._id,
          timestamp: new Date().toISOString()
        },
      };

      // Show local notification only
      const notificationResult = await this.showNotification(title, options);
      return notificationResult;
      
    } catch (error) {
      throw error;
    }
  }

  async notifySafetyIncident(area, incident) {
    const title = `Safety Incident: ${area.areaName}`;
    const options = {
      body: incident.description,
      data: {
        type: 'SAFETY',
        areaId: area._id,
        incidentId: incident._id
      },
      ...(this.swRegistration && {
        actions: [
          {
            action: 'view',
            title: 'View Details'
          }
        ]
      })
    };

    try {
      // Show local notification
      await this.showNotification(title, options);

      // Send to server
      await axios.post('/api/notifications/telegram', {
        type: 'SAFETY',
        priority: incident.severity || 'HIGH',
        content: incident.description,
        metadata: {
          area: area.areaName,
          type: incident.type,
          severity: incident.severity
        }
      });
    } catch (error) {
      throw error;
    }
  }

  async notifyInventoryAlert(part, alert) {
    const title = `Inventory Alert: ${part.partName}`;
    const options = {
      body: alert.message,
      data: {
        type: 'INVENTORY',
        partId: part._id,
        alertId: alert._id
      },
      ...(this.swRegistration && {
        actions: [
          {
            action: 'view',
            title: 'View Inventory'
          }
        ]
      })
    };

    try {
      await this.showNotification(title, options);

      await axios.post('/api/notifications/telegram', {
        type: 'INVENTORY',
        priority: alert.priority || 'MEDIUM',
        content: alert.message,
        metadata: {
          part: part.partName,
          currentStock: alert.currentStock,
          threshold: alert.threshold
        }
      });
    } catch (error) {
      throw error;
    }
  }

  // Helper method for queueing notifications
  queueNotification(title, options) {
    this.notificationQueue.push({ title, options });
  }

  // Cleanup method
  destroy() {
    if (this.audioContext) {
      this.audioContext.close();
    }
    clearInterval(this.processQueue);
    navigator.serviceWorker.removeEventListener('message', this.handleNotificationClick);
  }
}

// Create and export singleton instance
export const notificationService = new NotificationService();